// Season editor (New Season): pick a season (Pre season = template, Active,
// Archive), edit it, "Save changes" -> PUT v2/seasons/... (one PUT per season in
// bulk edit, writing only the fields the user changed, and each season's
// sensors as the sensors section left them).
// Pre season lists every block: its pre-season, or a copy of its current
// season that "Save changes" creates (POST v2/seasons/.../pre_season). The
// header's ⋮ "Activate now" makes a saved pre-season the block's active season
// (POST .../activate: V1 create, the previous active season archived). Its
// "Get data from" fills the form from another season, as unsaved edits.
const React = require('react');
const { useState, useEffect, useMemo, useCallback, useRef } = require('react');
const { useDispatch, useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const {
  save_new_season_season,
  load_new_season_season_detail,
  create_new_season_pre_season,
  activate_new_season_pre_season,
  delete_new_season_season,
} = require('@actions/newSeasonActions');
const fieldManager = require('fieldManager');
const { convert } = require('unitsManager');
const {
  findV2Distribution,
  findV2FarmingUnit,
  sortByName,
} = require('./v2Distributions');
const helpers = require('./seasonHelpers');
const { getBlockAreas } = require('./layoutHelpers');
const {
  buildSeasonSaveBody,
  buildPreSeasonCreateBody,
  buildActivationBody,
  dataFromSeason,
  parseSeasonField,
  withChanges,
} = require('./seasonSaveBody');
const GeneralClasses = require('GeneralClasses');
const { parseFarmingUnitSensors } = require('./sensorEditRules');
const { useSeasonSensors, sensorsOfDetail } = require('./useSeasonSensors');
const { seasonEditorUrl, BULK_EDIT_ENABLED } = require('./newSeasonConstants');
const {
  useToast,
  Toast,
  SegmentedTabs,
  Modal,
  ConfirmDeleteBlockModal,
} = require('./NewSeasonUi');
const BlocksMap = require('./BlocksMap');
const SeasonList = require('./SeasonList');
const GetDataFromModal = require('./GetDataFromModal');
const SensorsSection = require('./SensorsSection');
const {
  Section,
  GeneralSection,
  DatesSection,
  IrrigationSection,
  ContractSection,
} = require('./SeasonForm');
const {
  SeasonAdvanceSection,
  useAdvanceSection,
} = require('./SeasonAdvanceSection');

const { SEASON_TABS, SEASON_ACTIONS } = helpers;

// What Dashboard.onArchive changes on a field.
const ARCHIVE_CHANGES = Object.freeze({ 'settings.field_state': 'archive' });

// Distribution / farming-unit selects come from GET v2/distributions only.
const useDistributions = () => {
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  return useMemo(() => sortByName(distributions), [distributions]);
};

const EntitySelects = ({
  distributions,
  distributionId,
  farmingUnitId,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const distribution = findV2Distribution(distributions, distributionId);
  const farmingUnits = sortByName(distribution?.farming_units);
  const onDistribution = (id) => {
    const next = findV2Distribution(distributions, id);
    const firstFarmingUnit = sortByName(next?.farming_units)[0];
    if (firstFarmingUnit) {
      onNavigate(seasonEditorUrl(id, firstFarmingUnit.id));
    }
  };
  return (
    <div className='ns-entity-selects'>
      <div className='ns-select-entity'>
        <div className='ns-select-entity-title'>{t('distribution')}</div>
        <select
          name={'ns-select-entity-dist'}
          className='form-control ns-input custom-select'
          value={distributionId}
          aria-label={t('distribution')}
          onChange={(e) => onDistribution(e.target.value)}
        >
          {!distribution && (
            <option value={distributionId}>{distributionId}</option>
          )}
          {distributions.map((current) => (
            <option key={current.id} value={current.id}>
              {current.name}
            </option>
          ))}
        </select>
      </div>

      <div className='ns-select-entity'>
        <div className='ns-select-entity-title'>{t('farming_unit')}</div>
        <select
          name={'ns-select-entity-fu'}
          className='form-control custom-select ns-input'
          value={farmingUnitId}
          aria-label={t('farming_unit')}
          onChange={(e) =>
            onNavigate(seasonEditorUrl(distributionId, e.target.value))
          }
        >
          {!farmingUnits.some((current) => current.id === farmingUnitId) && (
            <option value={farmingUnitId}>{farmingUnitId}</option>
          )}
          {farmingUnits.map((current) => (
            <option key={current.id} value={current.id}>
              {current.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Selection: a single selected row, or the checked rows in bulk mode.
// `nextKeyRef`: the row to select once the tab changes (after "Activate now").
const useSeasonSelection = (rows, tab, nextKeyRef) => {
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  useEffect(() => {
    setSelectedKey(nextKeyRef.current);
    nextKeyRef.current = null;
    setCheckedKeys([]);
  }, [tab, bulkMode, nextKeyRef]);
  const rowByKey = useMemo(
    () => new Map(rows.map((row) => [row.key, row])),
    [rows],
  );
  const effectiveKey = rowByKey.has(selectedKey) ? selectedKey : rows[0]?.key;
  const activeRows = bulkMode
    ? checkedKeys.map((key) => rowByKey.get(key)).filter(Boolean)
    : [rowByKey.get(effectiveKey)].filter(Boolean);
  const toggle = useCallback(
    (key) =>
      setCheckedKeys((previous) =>
        previous.includes(key)
          ? previous.filter((current) => current !== key)
          : previous.concat([key]),
      ),
    [],
  );
  const toggleAll = () =>
    setCheckedKeys((previous) =>
      previous.length === rows.length ? [] : rows.map((row) => row.key),
    );
  return {
    bulkMode,
    setBulkMode,
    selectedKey: effectiveKey,
    setSelectedKey,
    checkedKeys,
    toggle,
    toggleAll,
    activeRows,
  };
};

// Same array instance while the active seasons are the same objects, so the
// form only resets when the selection changes or a season is re-saved.
const useStableSeasons = (activeRows) => {
  const previousRef = useRef([]);
  const seasons = activeRows.map((row) => row.season);
  const previous = previousRef.current;
  const isSame =
    previous.length === seasons.length &&
    seasons.every((season, index) => season === previous[index]);
  if (!isSame) {
    previousRef.current = seasons;
  }
  return previousRef.current;
};

// Form values for the active seasons, and the Advance section's Field path
// edits ({path: value}); both reset whenever the active seasons change.
// `changes()`: everything to save, form keys first (so an explicit
// recommendation display follows an irrigation type change).
const useSeasonForm = (activeRows) => {
  const seasons = useStableSeasons(activeRows);
  const initialValues = useMemo(() => helpers.commonValues(seasons), [seasons]);
  const [values, setValues] = useState(initialValues);
  const [fieldEdits, setFieldEdits] = useState({});
  // Counts replaceWith, for the Advance section's components.
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    setValues(initialValues);
    setFieldEdits({});
  }, [initialValues]);
  const changed = helpers.changedKeys(initialValues, values);
  const onChange = useCallback(
    (key, value) => setValues((previous) => ({ ...previous, [key]: value })),
    [],
  );
  const onChangeMany = useCallback(
    (patch) => setValues((previous) => ({ ...previous, ...patch })),
    [],
  );
  const onFieldEdit = useCallback(
    (path, value) =>
      setFieldEdits((previous) => ({ ...previous, [path]: value })),
    [],
  );
  // The stored season with these edits instead of the current ones.
  const replaceWith = useCallback(
    (nextValues, nextFieldEdits) => {
      setValues({ ...initialValues, ...nextValues });
      setFieldEdits(nextFieldEdits);
      setRevision((previous) => previous + 1);
    },
    [initialValues],
  );
  const isChanged = changed.length > 0 || Object.keys(fieldEdits).length > 0;
  const changes = () => ({ ...helpers.pick(values, changed), ...fieldEdits });
  return {
    values,
    initialValues,
    changed,
    fieldEdits,
    isChanged,
    changes,
    onChange,
    onChangeMany,
    onFieldEdit,
    revision,
    replaceWith,
  };
};

// GET v2/seasons/... for every active season:
// {[seasonId]: {season, farmingUnitSensors} | {error}}. The detail is the only
// source of a season's sensors (the tree has none) and of the farming unit's
// sensors that a Tal Gil sensor links to.
const useSeasonDetails = (distributionId, farmingUnitId, activeRows) => {
  const dispatch = useDispatch();
  const [details, setDetails] = useState({});
  // A pre-season not created yet reads the season it copies (if any).
  const seasonIds = activeRows.map(helpers.detailSeasonIdOf).filter(Boolean);
  const missingKey = seasonIds.filter((id) => !details[id]).join('|');
  const fetchDetail = useCallback(
    (seasonId) =>
      dispatch(
        load_new_season_season_detail(distributionId, farmingUnitId, seasonId),
      ).then((detail) => ({
        season: detail.season,
        farmingUnitSensors: detail.farming_unit_sensors,
      })),
    [dispatch, distributionId, farmingUnitId],
  );
  const setDetail = useCallback(
    (seasonId, entry) =>
      setDetails((previous) => ({ ...previous, [seasonId]: entry })),
    [],
  );
  useEffect(() => {
    missingKey
      .split('|')
      .filter(Boolean)
      .forEach((seasonId) =>
        fetchDetail(seasonId).then(
          (detail) => setDetail(seasonId, detail),
          (error) => setDetail(seasonId, { error: String(error) }),
        ),
      );
  }, [missingKey, fetchDetail, setDetail]);
  return { details, fetchDetail, setDetail };
};

// The V2 farming unit and its distribution's name: what V1 picks the default
// week start from (seasonSaveBody.firstDayOfWeekFor).
const useSeasonEntity = (distributionId, farmingUnitId) => {
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  return useMemo(
    () => ({
      farmingUnit: findV2FarmingUnit(
        distributions,
        distributionId,
        farmingUnitId,
      ),
      distributionName: findV2Distribution(distributions, distributionId)?.name,
    }),
    [distributions, distributionId, farmingUnitId],
  );
};

// Edit Field's required inputs for the selected seasons. Active / archived
// seasons must pass them to be saved; a pre-season may be saved partial and
// must pass them to be activated. The sow and pre-season irrigation start
// dates must be before every selected season's harvest date, for every save
// (pre-seasons too) and for activation; the error names the harvest date.
// `errors` block Save. `displayErrors` are the ones shown: a pre-season's missing
// inputs too once "Activate now" was refused (`revealErrors`), for that selection.
const useSeasonValidation = (activeRows, form) => {
  const values = form.values;
  const selectionKey = activeRows.map((row) => row.key).join('|');
  const [revealedKey, setRevealedKey] = useState(null);
  const lengthUnit = useSelector(
    (state) => state.authentication_data?.user_data?.length,
  );
  const allErrors = {
    ...helpers.validateSeasonValues(values, { lengthUnit }),
    ...helpers.areaSizeErrors(values, form.changed),
  };
  // Every selected season's harvest date must be after its sow and pre-season
  // irrigation start dates (the form's, else each season's own).
  const harvestErrors = helpers.harvestErrorsOf(
    activeRows.map((row) => row.season),
    values,
  );
  Object.keys(harvestErrors).forEach((key) => {
    if (!allErrors[key]) {
      allErrors[key] = harvestErrors[key];
    }
  });
  // A pre-season may be saved incomplete, but never with harvest first.
  const isTemplateOnly = activeRows.every(
    (row) => row.season.state === 'template',
  );
  const errors = isTemplateOnly ? harvestErrors : allErrors;
  const displayErrors = revealedKey === selectionKey ? allErrors : errors;
  const errorsOfSeason = (season) => ({
    ...helpers.validateSeasonValues(helpers.commonValues([season]), {
      lengthUnit,
      harvestDate: season?.crop_data?.harvest_date,
    }),
    ...helpers.entityErrors(season),
  });
  const revealErrors = () => setRevealedKey(selectionKey);
  return { errors, displayErrors, errorsOfSeason, revealErrors };
};

const saveSequentially = (rows, save) =>
  rows.reduce(
    (chain, row) =>
      chain.then((result) =>
        Promise.resolve(save(row))
          .then(() => ({ ...result, saved: result.saved + 1 }))
          .catch((error) => ({
            ...result,
            failed: result.failed.concat([row.block.name + ': ' + error]),
          })),
      ),
    Promise.resolve({ saved: 0, failed: [] }),
  );

// The rows "Save changes" writes: every active row when the form changed,
// otherwise only the seasons whose sensors changed — and always a pre-season
// that is not created yet (saving it as copied creates it).
const rowsToSave = (activeRows, form, seasonSensors) =>
  form.isChanged
    ? activeRows
    : activeRows.filter(
        (row) =>
          row.isNewPreSeason ||
          seasonSensors.editedSeasonIds.includes(row.season.id),
      );

// POST v2/seasons/.../pre_season: the block's pre-season, a copy of its
// current season with `body` (buildPreSeasonCreateBody) on top.
const createPreSeason = (dispatch, ids, row, body) =>
  Promise.resolve(
    dispatch(
      create_new_season_pre_season(ids.distributionId, ids.farmingUnitId, {
        block_id: row.block.id,
        ...body,
      }),
    ),
  );

// The season a pre-season not created yet copies, fresh before the POST: its
// source's detail, or — a block with no season — the empty copy itself.
const copiedSeasonOf = (seasonDetails, row) =>
  row.sourceSeasonId
    ? seasonDetails
        .fetchDetail(row.sourceSeasonId)
        .then((detail) => detail.season)
    : Promise.resolve(row.season);

const useSaveSeasons = ({
  distributionId,
  farmingUnitId,
  activeRows,
  form,
  seasonDetails,
  seasonSensors,
  blockingReason,
  validation,
  checkAdvance,
  reloadTree,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const [isSaving, setIsSaving] = useState(false);
  // Fresh detail right before each PUT. A season sends the sensors the user
  // left it with, or — untouched — its current ones as Edit Field would
  // send them; the PUT response (with sensors) refreshes the detail.
  const saveRow = (row, changes) =>
    row.isNewPreSeason
      ? copiedSeasonOf(seasonDetails, row).then((source) =>
          createPreSeason(
            dispatch,
            { distributionId, farmingUnitId },
            row,
            buildPreSeasonCreateBody(row.season, source, changes, entity),
          ),
        )
      : saveExistingRow(row, changes);
  const saveExistingRow = (row, changes) =>
    seasonDetails.fetchDetail(row.season.id).then((detail) => {
      const sensors =
        seasonSensors.editedSensorsOf(row.season.id) || sensorsOfDetail(detail);
      let body;
      try {
        body = buildSeasonSaveBody(detail.season, changes, entity, sensors);
      } catch (error) {
        return Promise.reject(t('new_season_sensors_missing'));
      }
      return Promise.resolve(
        dispatch(
          save_new_season_season(
            distributionId,
            farmingUnitId,
            row.season.id,
            body,
          ),
        ),
      ).then((saved) => {
        if (saved) {
          seasonDetails.setDetail(row.season.id, {
            season: saved,
            farmingUnitSensors: detail.farmingUnitSensors,
          });
        }
        seasonSensors.clear(row.season.id);
      });
    });
  const save = () => {
    // Edit Field's form checks the advanced inputs too.
    const isAdvanceValid = checkAdvance();
    if (Object.keys(validation.errors).length > 0 || !isAdvanceValid) {
      showToast(t('new_season_fix_errors'), 'error');
      return;
    }
    if (blockingReason) {
      showToast(blockingReason, 'error');
      return;
    }
    const rows = rowsToSave(activeRows, form, seasonSensors);
    if (rows.length === 0) {
      showToast(t('new_season_nothing_changed'), 'error');
      return;
    }
    const changes = form.changes();
    setIsSaving(true);
    saveSequentially(rows, (row) => saveRow(row, changes)).then(
      ({ saved, failed }) => {
        setIsSaving(false);
        // Created pre-seasons replace their copies once the tree is re-read.
        if (rows.some((row) => row.isNewPreSeason)) {
          reloadTree();
        }
        if (failed.length === 0) {
          showToast(
            rows.length > 1
              ? t('new_season_bulk_saved', { count: saved })
              : t('new_season_changes_saved'),
          );
        } else {
          showToast(
            t('new_season_bulk_partial', {
              saved,
              total: rows.length,
              failed: failed.join('; '),
            }),
            'error',
          );
        }
      },
    );
  };
  return { isSaving, save };
};

// The block's current active season, which "Activate now" archives.
const activeSeasonOf = (block) =>
  (block?.seasons || []).find((season) => season.state === 'active') || null;

// "Activate now": confirm, then build V1's create body from the pre-season's
// fresh detail and POST .../activate (which re-reads the tree). Unsaved edits
// must be saved first, so the activated season is what the user saw saved.
// A block whose pre-season is still the unsaved copy gets it created first
// (as "Save changes" would), then activated.
const useActivatePreSeason = ({
  distributionId,
  farmingUnitId,
  seasonDetails,
  errorsOfSeason,
  revealErrors,
  reloadTree,
  onActivated,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const [pendingRow, setPendingRow] = useState(null);
  const [isActivating, setIsActivating] = useState(false);
  // Why the season cannot be activated yet (a toast's text), or null.
  // `form`: {isDirty, isAdvanceEditing, isAdvanceLoaded, checkAdvance}.
  const blockingMessage = (row, form) => {
    if (form.isDirty) {
      return t('new_season_activate_save_first');
    }
    if (form.isAdvanceEditing) {
      return t('edit_field_finish_open_edit_message');
    }
    if (!form.isAdvanceLoaded) {
      return t('new_season_advance_loading');
    }
    // V1's create needs what Edit Field's Update requires: the Advance
    // section's inputs (the KC table) as well as the form's.
    const errors = errorsOfSeason(row.season);
    const isAdvanceValid = form.checkAdvance();
    if (Object.keys(errors).length === 0 && isAdvanceValid) {
      return null;
    }
    revealErrors();
    if (errors.sow_date?.id) {
      return t(errors.sow_date.id, errors.sow_date.params);
    }
    if (Object.keys(errors).length === 0) {
      return t('new_season_activate_advance_incomplete');
    }
    return t(errors.areas || 'new_season_activate_incomplete');
  };
  const request = (row, form) => {
    const message = blockingMessage(row, form);
    if (message) {
      showToast(message, 'error');
      return;
    }
    setPendingRow(row);
  };
  const cancel = useCallback(() => setPendingRow(null), []);
  // The saved pre-season's id: the row's, or a pre-season created now.
  const savedPreSeasonId = (row) =>
    row.isNewPreSeason
      ? createPreSeason(
          dispatch,
          { distributionId, farmingUnitId },
          row,
          {},
        ).then((season) => season.id)
      : Promise.resolve(row.season.id);
  const activate = (seasonId) =>
    seasonDetails
      .fetchDetail(seasonId)
      .then((detail) =>
        dispatch(
          activate_new_season_pre_season(
            distributionId,
            farmingUnitId,
            seasonId,
            buildActivationBody(
              detail.season,
              entity,
              Math.floor(Date.now() / 1000),
            ),
          ),
        ),
      );
  const confirm = () => {
    const row = pendingRow;
    let createdId = null;
    setIsActivating(true);
    savedPreSeasonId(row)
      .then((seasonId) => {
        createdId = row.isNewPreSeason ? seasonId : null;
        return activate(seasonId).then(() => seasonId);
      })
      .then((seasonId) => {
        showToast(t('new_season_activated', { name: row.block.name }));
        onActivated(seasonId);
      })
      .catch((error) => {
        // Created but not activated: show the saved pre-season.
        if (createdId) {
          reloadTree();
        }
        showToast(String(error), 'error');
      })
      .finally(() => {
        setIsActivating(false);
        setPendingRow(null);
      });
  };
  return { pendingRow, isActivating, request, cancel, confirm };
};

// "Get data from" (season header ⋮, Pre season tab, one block): the chosen
// season's data replaces the form's, as unsaved edits. The user's own unsaved
// edits go with it, once they agree to lose them. Dates and sensors stay the
// block's (seasonHelpers.DATA_SOURCE_*).
const useGetDataFrom = ({
  distributionId,
  farmingUnitId,
  form,
  seasonDetails,
  seasonSensors,
  hasUnsavedEdits,
  isModerator,
  onApplied,
  showToast,
  t,
}) => {
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const [row, setRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const close = useCallback(() => setRow(null), []);
  const select = (seasonId) => {
    if (hasUnsavedEdits && !window.confirm(t('new_season_get_data_discard'))) {
      return;
    }
    setIsLoading(true);
    seasonDetails
      .fetchDetail(seasonId)
      .then((detail) => {
        const { values, fieldEdits } = dataFromSeason(detail.season, entity, {
          isModerator,
          currentType: form.initialValues.irrigation_type,
        });
        form.replaceWith(values, fieldEdits);
        seasonSensors.clear(row.season.id);
        onApplied();
        close();
        showToast(t('new_season_get_data_copied'));
      })
      .catch((error) =>
        showToast(
          t('new_season_detail_failed', { message: String(error) }),
          'error',
        ),
      )
      .finally(() => setIsLoading(false));
  };
  return { row, isLoading, open: setRow, close, select };
};

// ⋮ Archive / Delete (Active and Archive tabs, one season): confirm, then write
// as the dashboard does (Archive: V1's update with `field_state: archive` and the
// season's sensors; Delete: V1's soft delete). The user's unsaved edits are lost,
// once they agree. `onRemoved(the row to select)` runs before the tree re-read.
// A block's only season: the API shows no block without a live season, so the
// block gets a new block's pre-season right after (name and polygon only; the
// server copies nothing, the block having no live season left). Where pre-seasons
// cannot be created (`hierarchy_writes_enabled`), that delete is refused.
const useSeasonRemoval = ({
  distributionId,
  farmingUnitId,
  rows,
  canCreatePreSeason,
  seasonDetails,
  hasUnsavedEdits,
  reloadTree,
  onRemoved,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const [pending, setPending] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const request = (row, action) => {
    if (
      action === SEASON_ACTIONS.DELETE &&
      helpers.isOnlySeason(row) &&
      !canCreatePreSeason
    ) {
      showToast(t('new_season_delete_only_season_unavailable'), 'error');
      return;
    }
    if (hasUnsavedEdits && !window.confirm(t('new_season_discard_changes'))) {
      return;
    }
    setPending({ row, action });
  };
  const cancel = useCallback(() => setPending(null), []);
  const archive = (seasonId) =>
    seasonDetails.fetchDetail(seasonId).then((detail) => {
      let body;
      try {
        body = buildSeasonSaveBody(
          detail.season,
          ARCHIVE_CHANGES,
          entity,
          sensorsOfDetail(detail),
        );
      } catch (error) {
        return Promise.reject(t('new_season_sensors_missing'));
      }
      return dispatch(
        save_new_season_season(distributionId, farmingUnitId, seasonId, body),
      );
    });
  const remove = (row) =>
    Promise.resolve(
      dispatch(
        delete_new_season_season(distributionId, farmingUnitId, row.season.id),
      ),
    ).then(() =>
      helpers.isOnlySeason(row)
        ? Promise.resolve(
            dispatch(
              create_new_season_pre_season(distributionId, farmingUnitId, {
                block_id: row.block.id,
                geo_data: helpers.newBlockGeoData(row.season),
              }),
            ),
          ).catch((error) =>
            Promise.reject(
              t('new_season_delete_pre_season_failed', { message: error }),
            ),
          )
        : undefined,
    );
  const confirm = () => {
    if (isWorking) {
      return;
    }
    const { row, action } = pending;
    const isArchive = action === SEASON_ACTIONS.ARCHIVE;
    setIsWorking(true);
    Promise.resolve(isArchive ? archive(row.season.id) : remove(row))
      .then(() => {
        onRemoved(helpers.neighborKeyOf(rows, row.key));
        return reloadTree();
      })
      .then(() =>
        showToast(
          t(isArchive ? 'new_season_archived' : 'new_season_deleted', {
            name: row.block.name,
          }),
        ),
      )
      .catch((error) => showToast(String(error), 'error'))
      .finally(() => {
        setIsWorking(false);
        setPending(null);
      });
  };
  return { pending, isWorking, request, cancel, confirm };
};

// Archive's confirm, and Delete's (the farming unit editor's delete dialog).
const SeasonRemovalModal = ({ removal, tab }) => {
  const { t } = useTranslation();
  const { pending, isWorking } = removal;
  const onCancel = isWorking ? () => {} : removal.cancel;
  const name = pending ? helpers.seasonTitle(pending.row, tab) : '';
  if (pending?.action === SEASON_ACTIONS.DELETE) {
    return (
      <ConfirmDeleteBlockModal
        isOpen
        title={t('new_season_delete_season_title', { name })}
        text={t(
          helpers.isOnlySeason(pending.row)
            ? 'new_season_delete_only_season_text'
            : 'new_season_delete_season_text',
        )}
        onCancel={onCancel}
        onConfirm={removal.confirm}
      />
    );
  }
  return (
    <Modal
      isOpen={Boolean(pending)}
      onClose={onCancel}
      className='ns-modal-small'
    >
      <div className='ns-modal-header'>
        {t('new_season_archive_season_title', { name })}
      </div>
      <div className='ns-modal-body ns-modal-text'>
        {t('new_season_archive_season_text')}
      </div>
      <div className='ns-modal-footer'>
        <button
          type='button'
          className='ns-btn ns-btn-outline'
          disabled={isWorking}
          onClick={onCancel}
        >
          {t('cancel')}
        </button>
        <button
          type='button'
          className='ns-btn ns-btn-primary'
          disabled={isWorking}
          onClick={removal.confirm}
        >
          {t('dashboard_move_to_archive')}
          {isWorking && (
            <span
              className='spinner-border spinner-border-sm ml-2'
              role='status'
            />
          )}
        </button>
      </div>
    </Modal>
  );
};

// "Block 1 / Sep 2025" (or just the block) for the season being archived.
const archivedSeasonLabel = (block, season) => {
  const month = helpers.formatSeasonMonth(season.crop_data?.sow_date);
  return month ? `${block.name} / ${month}` : block.name;
};

const ActivateModal = ({ activation, tab }) => {
  const { t } = useTranslation();
  const row = activation.pendingRow;
  const current = activeSeasonOf(row?.block);
  return (
    <Modal
      isOpen={Boolean(row)}
      onClose={activation.isActivating ? () => {} : activation.cancel}
      className='ns-modal-small'
    >
      <div className='ns-modal-header'>
        {t('new_season_activate_title', {
          name: row ? helpers.seasonTitle(row, tab) : '',
        })}
      </div>
      <div className='ns-modal-body ns-modal-text'>
        {current
          ? t('new_season_activate_text_archive', {
              season: archivedSeasonLabel(row.block, current),
            })
          : t('new_season_activate_text_no_active')}{' '}
        {t('new_season_activate_text_sensors')}
      </div>
      <div className='ns-modal-footer'>
        <button
          type='button'
          className='ns-btn ns-btn-outline'
          disabled={activation.isActivating}
          onClick={activation.cancel}
        >
          {t('cancel')}
        </button>
        <button
          type='button'
          className='ns-btn ns-btn-primary'
          disabled={activation.isActivating}
          onClick={activation.confirm}
        >
          {t('new_season_activate_now')}
          {activation.isActivating && (
            <span
              className='spinner-border spinner-border-sm ml-2'
              role='status'
            />
          )}
        </button>
      </div>
    </Modal>
  );
};

// The season header's ⋮ menu. items: [{key, label, onClick, isDanger}]; a danger
// item is red, under a divider.
const SeasonMenu = ({ items }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className='ns-season-menu'>
      <button
        type='button'
        className='ns-season-menu-btn'
        title={t('new_season_season_actions')}
        aria-label={t('new_season_season_actions')}
        onClick={() => setIsOpen((open) => !open)}
      >
        ⋮
      </button>
      {isOpen && (
        <div
          className='ns-season-menu-list'
          onMouseLeave={() => setIsOpen(false)}
        >
          {items.map((item) => (
            <React.Fragment key={item.key}>
              {item.isDanger && items.length > 1 && (
                <div className='ns-season-menu-divider' />
              )}
              <button
                type='button'
                className={item.isDanger ? 'ns-season-menu-danger' : undefined}
                onClick={() => {
                  setIsOpen(false);
                  item.onClick();
                }}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// The farming unit's sensors (the same in every season's detail).
const useFarmingUnitSensors = (details) => {
  const json = Object.values(details).find(
    (detail) => detail?.farmingUnitSensors,
  )?.farmingUnitSensors;
  return useMemo(() => parseFarmingUnitSensors(json, GeneralClasses), [json]);
};

const useSensorsProps = ({
  distributions,
  distributionId,
  farmingUnitId,
  details,
  seasonSensors,
  onBlockingChange,
}) => {
  const farmingUnitSensors = useFarmingUnitSensors(details);
  return {
    sensorsBySeason: seasonSensors.sensorsBySeason,
    farmingUnitSensors,
    onUpdateSensors: seasonSensors.update,
    onBlockingChange,
    farmingUnitName: findV2FarmingUnit(
      distributions,
      distributionId,
      farmingUnitId,
    )?.name,
    distributionName: findV2Distribution(distributions, distributionId)?.name,
  };
};

// The selected season parsed as V1 parses a field, with the form's changes
// applied, for the Advance section: one created season at a time, once its
// detail is loaded. `unavailableMessage` says why there is none.
// The season the Advance section edits: the row's detail, or — a pre-season
// not created yet — the detail of the season it copies (a block with no
// season: the empty copy itself).
const advanceSeasonOf = (row, details) => {
  if (!row) {
    return null;
  }
  const detailId = helpers.detailSeasonIdOf(row);
  return detailId ? (details[detailId]?.season ?? null) : row.season;
};

const useAdvanceField = ({ singleRow, details, entity, form }) => {
  const { t } = useTranslation();
  const detailSeason = advanceSeasonOf(singleRow, details);
  // Re-parsed only for a new detail (a selection change or a save).
  const base = useMemo(
    () => (detailSeason ? parseSeasonField(detailSeason, entity) : null),
    [detailSeason, entity],
  );
  const { values, fieldEdits } = form;
  const field = useMemo(
    () => (base ? withChanges(base, form.changes()) : null),
    [base, values, fieldEdits], // eslint-disable-line react-hooks/exhaustive-deps
  );
  if (field) {
    return { field, unavailableMessage: null };
  }
  if (!singleRow) {
    return { field: null, unavailableMessage: t('new_season_advance_bulk') };
  }
  return { field: null, unavailableMessage: t('new_season_advance_loading') };
};

// The selected seasons' contract installation date (MULTIPLE_VALUES when they
// differ).
const installationDateOf = (activeRows) => {
  const dates = activeRows.map(
    (row) => row.season.contract_data?.installation_date ?? null,
  );
  return dates.every((date) => date === dates[0])
    ? dates[0]
    : helpers.MULTIPLE_VALUES;
};

// UpdateFieldModal.calculateArea: the polygon's area in the user's unit.
const usePolygonArea = (activeRows) => {
  const areaUnit = useSelector(
    (state) => state.authentication_data?.user_data?.area,
  );
  const areas =
    activeRows.length === 1 ? activeRows[0].season.geo_data?.areas : null;
  return useMemo(
    () =>
      areas?.length
        ? convert(fieldManager.getFieldArea(areas), 'dunam', areaUnit, 'area')
        : null,
    [areas, areaUnit],
  );
};

// UpdateFieldModal: the latest measured irrigation rate, when the season has
// one.
const measuredRateOf = (season) => {
  const values = season?.historical_data?.measured_irrigation_rate?.values;
  return values ? Object.values(values)[0] : null;
};

const useMapBlocks = (tree) =>
  useMemo(
    () =>
      (tree.field_groups || []).reduce(
        (blocks, fieldGroup) =>
          blocks.concat(
            (fieldGroup.blocks || []).map((block) => ({
              key: block.id,
              name: block.name,
              areas: getBlockAreas(block),
            })),
          ),
        [],
      ),
    [tree],
  );

const SeasonFormPanel = ({
  tab,
  isTabEmpty,
  activeRows,
  menuItems,
  form,
  errors,
  mapBlocks,
  onMapBlockClick,
  isSaving,
  onSave,
  seasonDetails,
  sensorsProps,
  fitKey,
  isModerator,
  advance,
  advanceField,
  onAdvanceEditingChange,
}) => {
  const { t } = useTranslation();
  const selectedBlockKeys = useMemo(
    () => activeRows.map((row) => row.block.id),
    [activeRows],
  );
  const polygonArea = usePolygonArea(activeRows);
  if (activeRows.length === 0) {
    return (
      <div className='ns-card ns-right-card ns-center-message'>
        {t(
          isTabEmpty
            ? helpers.emptyTabMessageKey(tab)
            : 'new_season_select_season',
        )}
      </div>
    );
  }
  const isTemplate = activeRows.every((row) => row.season.state === 'template');
  return (
    <div className='ns-card ns-right-card ns-season-form'>
      <div className='ns-title-bar ns-title-static'>
        {helpers.seasonTitle(activeRows[0], tab)}
        {menuItems.length > 0 && <SeasonMenu items={menuItems} />}
      </div>
      <div className='ns-form-scroll'>
        <Section
          title={t('new_season_section_map')}
          className={' d-flex flex-column'}
        >
          <BlocksMap
            className='ns-map-medium'
            blocks={mapBlocks}
            selectedKeys={selectedBlockKeys}
            focusKeys={selectedBlockKeys}
            fitKey={fitKey}
            onBlockClick={onMapBlockClick}
          />
        </Section>
        <GeneralSection
          values={form.values}
          errors={errors}
          onChange={form.onChange}
          onChangeMany={form.onChangeMany}
          isModerator={isModerator}
          polygonArea={polygonArea}
        />
        <DatesSection
          values={form.values}
          errors={errors}
          onChange={form.onChange}
          isModerator={isModerator}
        />
        <IrrigationSection
          values={form.values}
          errors={errors}
          onChange={form.onChange}
          onChangeMany={form.onChangeMany}
          isModerator={isModerator}
          measuredRate={
            activeRows.length === 1
              ? measuredRateOf(
                  seasonDetails.details[activeRows[0].season.id]?.season,
                )
              : null
          }
        />
        {!isTemplate && (
          <SensorsSection
            {...sensorsProps}
            rows={activeRows}
            details={seasonDetails.details}
            mapBlocks={mapBlocks}
            selectedBlockKeys={selectedBlockKeys}
          />
        )}
        <ContractSection
          values={form.values}
          onChange={form.onChange}
          installationDate={installationDateOf(activeRows)}
          isModerator={isModerator}
        />
        <SeasonAdvanceSection
          advance={advance}
          revision={form.revision}
          field={advanceField.field}
          unavailableMessage={advanceField.unavailableMessage}
          isModerator={isModerator}
          farmingUnitSensors={sensorsProps.farmingUnitSensors}
          onFieldEdit={form.onFieldEdit}
          onEditingChange={onAdvanceEditingChange}
        />
      </div>
      <div className='ns-sticky-footer'>
        <button
          type='button'
          className='ns-btn ns-btn-primary'
          disabled={isSaving}
          onClick={onSave}
        >
          {t('new_season_save_changes')}
          {isSaving && (
            <span
              className='spinner-border spinner-border-sm ml-2'
              role='status'
            />
          )}
        </button>
      </div>
    </div>
  );
};

const SeasonEditor = ({
  distributionId,
  farmingUnitId,
  tree,
  reloadTree,
  setDirty,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const distributions = useDistributions();
  const [tab, setTab] = useState(SEASON_TABS.ACTIVE);
  const nextKeyRef = useRef(null);
  const groups = useMemo(
    () => helpers.buildSeasonGroups(tree, tab),
    [tree, tab],
  );
  const rows = useMemo(() => helpers.flattenRows(groups), [groups]);
  const selection = useSeasonSelection(rows, tab, nextKeyRef);
  const form = useSeasonForm(selection.activeRows);
  const { toast, showToast } = useToast();
  const seasonDetails = useSeasonDetails(
    distributionId,
    farmingUnitId,
    selection.activeRows,
  );
  const seasonSensors = useSeasonSensors(
    selection.activeRows,
    seasonDetails.details,
  );
  const [blockingReason, setBlockingReason] = useState(null);
  // Edit Field blocks saving while a planner timeframe is mid-edit.
  const [isAdvanceEditing, setIsAdvanceEditing] = useState(false);
  const advance = useAdvanceSection();
  const { clearMarks: clearAdvanceMarks } = advance;
  // The same form shows the next season: it starts unmarked.
  useEffect(() => {
    clearAdvanceMarks();
  }, [selection.selectedKey, clearAdvanceMarks]);
  const isDirty = form.isChanged || seasonSensors.isDirty;
  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);
  const validation = useSeasonValidation(selection.activeRows, form);
  const isTemplateOnly = selection.activeRows.every(
    (row) => row.season.state === 'template',
  );
  const { isSaving, save } = useSaveSeasons({
    distributionId,
    farmingUnitId,
    activeRows: selection.activeRows,
    form,
    seasonDetails,
    seasonSensors,
    blockingReason:
      blockingReason ||
      (isAdvanceEditing ? t('edit_field_finish_open_edit_message') : null),
    validation,
    // A pre-season may be saved partial, as with the other inputs.
    checkAdvance: () => isTemplateOnly || advance.check(),
    reloadTree,
    showToast,
    t,
  });
  // After "Activate now": the Active tab, with the new season selected.
  const activation = useActivatePreSeason({
    distributionId,
    farmingUnitId,
    seasonDetails,
    errorsOfSeason: validation.errorsOfSeason,
    revealErrors: validation.revealErrors,
    reloadTree,
    onActivated: (seasonId) => {
      nextKeyRef.current = seasonId;
      setTab(SEASON_TABS.ACTIVE);
    },
    showToast,
    t,
  });
  const singleRow =
    !selection.bulkMode && selection.activeRows.length === 1
      ? selection.activeRows[0]
      : null;
  const isModerator = tree.role === 'moderator';
  const getData = useGetDataFrom({
    distributionId,
    farmingUnitId,
    form,
    seasonDetails,
    seasonSensors,
    hasUnsavedEdits: isDirty || isAdvanceEditing,
    isModerator,
    // The planner starts over, so no timeframe is mid-edit any more.
    onApplied: () => setIsAdvanceEditing(false),
    showToast,
    t,
  });
  const removal = useSeasonRemoval({
    distributionId,
    farmingUnitId,
    rows,
    canCreatePreSeason: Boolean(tree.hierarchy_writes_enabled),
    seasonDetails,
    hasUnsavedEdits: isDirty || isAdvanceEditing,
    reloadTree,
    onRemoved: selection.setSelectedKey,
    showToast,
    t,
  });
  const entity = useSeasonEntity(distributionId, farmingUnitId);
  const advanceField = useAdvanceField({
    singleRow,
    details: seasonDetails.details,
    entity,
    form,
  });
  const sensorsProps = useSensorsProps({
    distributions,
    distributionId,
    farmingUnitId,
    details: seasonDetails.details,
    seasonSensors,
    onBlockingChange: setBlockingReason,
  });
  const mapBlocks = useMapBlocks(tree);
  const menuActions = {
    [SEASON_ACTIONS.GET_DATA]: {
      label: t('new_season_get_data_from'),
      onClick: () => getData.open(singleRow),
    },
    [SEASON_ACTIONS.ACTIVATE]: {
      label: t('new_season_activate_now'),
      onClick: () =>
        !activation.isChecking &&
        activation.request(singleRow, {
          isDirty,
          isAdvanceEditing,
          isAdvanceLoaded: Boolean(advanceField.field),
          checkAdvance: advance.check,
        }),
    },
    [SEASON_ACTIONS.ARCHIVE]: {
      label: t('dashboard_move_to_archive'),
      onClick: () => removal.request(singleRow, SEASON_ACTIONS.ARCHIVE),
    },
    [SEASON_ACTIONS.DELETE]: {
      label: t('dashboard_delete_field'),
      isDanger: true,
      onClick: () => removal.request(singleRow, SEASON_ACTIONS.DELETE),
    },
  };
  const menuItems = singleRow
    ? helpers.seasonActionsOf(tab).map((key) => ({ key, ...menuActions[key] }))
    : [];

  const onMapBlockClick = (blockId) => {
    const row = rows.find((current) => current.block.id === blockId);
    if (row) {
      selection.bulkMode
        ? selection.toggle(row.key)
        : selection.setSelectedKey(row.key);
    }
  };
  const tabs = [
    { value: SEASON_TABS.PRE_SEASON, label: t('new_season_pre_season') },
    { value: SEASON_TABS.ACTIVE, label: t('new_season_active') },
    { value: SEASON_TABS.ARCHIVE, label: t('new_season_archive') },
  ];
  return (
    <div className='ns-editor'>
      <div className='ns-left-card'>
        <div className='ns-card-title'>{t('new_season_manage_seasons')}</div>
        <EntitySelects
          distributions={distributions}
          distributionId={distributionId}
          farmingUnitId={farmingUnitId}
          onNavigate={onNavigate}
        />
        <SegmentedTabs options={tabs} value={tab} onChange={setTab} />
        {BULK_EDIT_ENABLED && (
          <label className='ns-toggle'>
            <input
              type='checkbox'
              checked={selection.bulkMode}
              onChange={() => selection.setBulkMode((on) => !on)}
            />
            <span>
              {selection.bulkMode
                ? t('new_season_bulk_edit')
                : t('new_season_bulk_select')}
            </span>
          </label>
        )}
        <div className='ns-list-scroll'>
          <SeasonList
            tab={tab}
            groups={groups}
            selectedKey={selection.selectedKey}
            bulkMode={selection.bulkMode}
            checkedKeys={selection.checkedKeys}
            onSelect={selection.setSelectedKey}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
          />
        </div>
      </div>
      <div className='ns-right-column'>
        <SeasonFormPanel
          tab={tab}
          isTabEmpty={rows.length === 0}
          activeRows={selection.activeRows}
          menuItems={menuItems}
          form={form}
          errors={validation.displayErrors}
          mapBlocks={mapBlocks}
          onMapBlockClick={onMapBlockClick}
          isSaving={isSaving}
          onSave={save}
          seasonDetails={seasonDetails}
          sensorsProps={sensorsProps}
          fitKey={distributionId + '/' + farmingUnitId}
          isModerator={isModerator}
          advance={advance}
          advanceField={advanceField}
          onAdvanceEditingChange={setIsAdvanceEditing}
        />
      </div>
      <ActivateModal activation={activation} tab={tab} />
      <SeasonRemovalModal removal={removal} tab={tab} />
      {getData.row && (
        <GetDataFromModal
          tree={tree}
          blockId={getData.row.block.id}
          isLoading={getData.isLoading}
          onCancel={getData.close}
          onSelect={getData.select}
        />
      )}
      <Toast toast={toast} />
    </div>
  );
};

module.exports = SeasonEditor;
